/**
 * Selbstheilendes Abo auf `GET /telephony/events` (SSE), ohne React.
 *
 * Zwei Lehren aus #1226:
 *  - EventSource verbindet nur bei vorübergehenden Fehlern selbst neu. Schließt
 *    der Proxy mit einem HTTP-Fehler (504 nach 75 s Stille), ist die Verbindung
 *    endgültig zu (`readyState === CLOSED`) — dann bauen wir sie selbst neu auf,
 *    mit wachsendem Abstand, damit ein liegendes Backend keinen Anfrage-Sturm
 *    abbekommt.
 *  - Der Server sendet alle 25 s einen Ping. Kommt lange gar nichts (auch kein
 *    Ping), hängt die Verbindung still — auch dann neu aufbauen.
 */

import type { TelephonyEvent } from "./telephonyService";
import { ereignisLesen, wiederverbindenNach } from "../components/telephony/tischtelefon";

/** Ohne jeden Frame (Ping kommt alle 25 s) gilt die Verbindung danach als tot. */
export const STILLE_MAX_MS = 70_000;

const CLOSED = 2;

/** Das, was wir von `EventSource` brauchen — in Tests durch ein Double ersetzbar. */
export interface EreignisQuelle {
    readonly readyState: number;
    onopen: ((ev: Event) => unknown) | null;
    onerror: ((ev: Event) => unknown) | null;
    onmessage: ((ev: MessageEvent) => unknown) | null;
    close(): void;
}

export interface EreignisAboDeps {
    oeffnen: () => EreignisQuelle | null;
    setTimeout: (fn: () => void, ms: number) => unknown;
    clearTimeout: (h: unknown) => void;
}

export interface EreignisAboHandler {
    ereignis: (evt: TelephonyEvent) => void;
    /** Nach einem NEUAUFBAU wieder verbunden — Ereignisse dazwischen können fehlen. */
    wiederVerbunden?: () => void;
}

/**
 * Abonniert den Strom und hält ihn am Leben. Gibt die Abmeldung zurück.
 * Heartbeats und unlesbare Frames erreichen den Handler nicht.
 */
export function anrufEreignisseAbonnieren(handler: EreignisAboHandler, deps: EreignisAboDeps): () => void {
    let quelle: EreignisQuelle | null = null;
    let wiederTimer: unknown = null;
    let stilleTimer: unknown = null;
    let beendet = false;
    let versuch = 0;
    let warSchonOffen = false;

    const stilleUeberwachen = () => {
        if (stilleTimer != null) deps.clearTimeout(stilleTimer);
        stilleTimer = deps.setTimeout(() => {
            stilleTimer = null;
            neuAufbauen();
        }, STILLE_MAX_MS);
    };

    const schliessen = () => {
        if (!quelle) return;
        quelle.onopen = null;
        quelle.onerror = null;
        quelle.onmessage = null;
        try {
            quelle.close();
        } catch {
            /* ignore */
        }
        quelle = null;
    };

    const neuAufbauen = () => {
        if (beendet) return;
        schliessen();
        if (stilleTimer != null) {
            deps.clearTimeout(stilleTimer);
            stilleTimer = null;
        }
        if (wiederTimer != null) return;
        const warte = wiederverbindenNach(versuch);
        versuch += 1;
        wiederTimer = deps.setTimeout(() => {
            wiederTimer = null;
            oeffnen();
        }, warte);
    };

    const oeffnen = () => {
        if (beendet) return;
        try {
            quelle = deps.oeffnen();
        } catch {
            quelle = null;
        }
        if (!quelle) {
            // EventSource nicht verfügbar — später erneut versuchen, nie werfen.
            neuAufbauen();
            return;
        }
        const q = quelle;
        q.onopen = () => {
            versuch = 0;
            stilleUeberwachen();
            if (warSchonOffen) handler.wiederVerbunden?.();
            warSchonOffen = true;
        };
        q.onmessage = (ev: MessageEvent) => {
            stilleUeberwachen();
            const evt = ereignisLesen(ev.data);
            if (!evt) return;
            try {
                handler.ereignis(evt);
            } catch {
                /* ein fehlerhafter Handler darf den Strom nicht abreißen */
            }
        };
        q.onerror = () => {
            // CONNECTING = der Browser verbindet selbst neu; nur CLOSED ist endgültig.
            if (q.readyState === CLOSED) neuAufbauen();
        };
        stilleUeberwachen();
    };

    oeffnen();

    return () => {
        beendet = true;
        if (wiederTimer != null) deps.clearTimeout(wiederTimer);
        if (stilleTimer != null) deps.clearTimeout(stilleTimer);
        wiederTimer = null;
        stilleTimer = null;
        schliessen();
    };
}
